import { Button, Card, CardContent, CardProps, Container, Grid, Typography, styled } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChildNameHeading from 'components/SiblingSwitch/ChildNameHeading';
import { ROUTE } from 'route/RouteEnums';
import { usePatientIsAdult } from './usePatientIsAdult';

/** MM-1131: Jotform used by both the Family & Friends Authorization and Designate a Care Contact tiles. */
const AUTHORIZATION_FORM_URL = 'https://form.jotform.com/243566346947167';

/** Same tile style as the Settings and Billing pages. */
const StyleCard = styled(Card)<CardProps>(() => ({
  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
  border: '1px solid #EFF4FF',
  borderRadius: 12,
  background: '#FFF',
  margin: '12px 16px',
  position: 'relative',
  zIndex: 99,
  padding: 20,
  '& .MuiTypography-root': {
    position: 'relative',
  },
  '& .MuiCardContent-root:last-child': {
    padding: 0,
  },
  /**
   * MM-1132 feedback: tile titles are 17px (was 20px).
   */
  '.MuiTypography-h2': {
    fontSize: 17,
  },
}));

/**
 * One Account Management tile.
 * title       - tile heading
 * description - text under the heading
 * buttonLabel - action button text
 * onClick     - in-app action (used when href is not set)
 * href        - external link, opened in a new tab
 */
interface AccountTileProps {
  title: string;
  description: string;
  buttonLabel: string;
  onClick?: () => void;
  href?: string;
}

/**
 * MM-1131: a tile laid out like the Settings tiles: heading, description on the left, action on the right.
 */
const AccountTile = ({ title, description, buttonLabel, onClick, href }: AccountTileProps) => (
  <Grid item xs={12}>
    <StyleCard>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'} gap={1.5}>
            <Typography variant='h2'>{title}</Typography>
            <Grid container>
              <Grid item xs={12} sm>
                <Typography variant='subtitle1'>{description}</Typography>
              </Grid>
              {/* MM-1132 feedback: on phones the link goes under the text, like Request Your Balance */}
              <Grid item xs={12} sm='auto' alignItems={'end'} textAlign={'end'} mt={{ xs: 1, sm: 0 }} pl={{ sm: 2 }}>
                {href ? (
                  <Button
                    variant='text'
                    size='small'
                    sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {buttonLabel}
                  </Button>
                ) : (
                  <Button
                    variant='text'
                    size='small'
                    sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                    onClick={onClick}
                  >
                    {buttonLabel}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </StyleCard>
  </Grid>
);

/**
 * MM-1131: Account Management page (left navigation "Account Management") with three tiles:
 * Contact Information & Account Ownership (existing request page), Family & Friends Authorization
 * and Designate a Care Contact (both open the authorization Jotform in a new tab).
 * MM-1132: Designate a Care Contact is shown only when the patient is 18 or older.
 */
const AccountManagementHome = () => {
  const navigate = useNavigate();
  /** MM-1132: whether the open patient is 18 or older; null while loading. */
  const isPatientAdult = usePatientIsAdult();

  return (
    <Container component='main' maxWidth='md'>
      <Grid container>
        <Grid item xs={10} display={'flex'} alignItems={'center'}>
          {/* MM-1123: same top space and child-name heading as the other pages */}
          <Typography variant='h5' gutterBottom sx={{ paddingTop: 6 }}>
            <ChildNameHeading title='Account Management' />
          </Typography>
        </Grid>

        {/* MM-1131: Tile 1 renamed from "Account Management" to "Contact Information & Account Ownership" */}
        <AccountTile
          title='Contact Information & Account Ownership'
          description={
            'Submit a request to update the primary email address or phone number on your account, ' +
            'or transfer account ownership.'
          }
          /* MM-1131 feedback: label given by Dr. Bansal in the ticket */
          buttonLabel='SUBMIT & TRACK REQUEST'
          onClick={() => navigate(ROUTE.ACCOUNT_MANAGEMENT)}
        />
        <AccountTile
          title='Family & Friends Authorization'
          description='Authorize family members or friends to receive information about your or your child’s care.'
          buttonLabel='AUTHORIZE INFORMATION SHARING'
          href={AUTHORIZATION_FORM_URL}
        />
        {/* MM-1132: shown only when the patient is 18 or older */}
        {isPatientAdult === true && (
          <AccountTile
            title='Designate a Care Contact'
            description={
              'If you are age 18 or older and own your account, authorize a parent or another trusted person ' +
              'to help with prescription requests, scheduling, forms, and other routine care matters.'
            }
            buttonLabel='DESIGNATE A CAREGIVER'
            href={AUTHORIZATION_FORM_URL}
          />
        )}
      </Grid>
    </Container>
  );
};

export default AccountManagementHome;
